<template>
  <div>
    <!-- Content Container -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div 
          v-for="product in (products?.data || [])" 
          :key="product.id" 
          class="p-4 hover:bg-gray-50 transition-colors"
        >
          <Link :href="`/products/${product.id}`" class="block space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
                <img 
                  v-if="product.photo_url || product.photo"
                  :src="product.photo_url || `/img/${product.photo}`"
                  :alt="product.name"
                  class="w-full h-full object-cover"
                  @error="$event.target.style.display='none'; $event.target.nextElementSibling.style.display='flex'"
                />
                <div :style="(product.photo_url || product.photo) ? 'display: none' : 'display: flex'" class="w-full h-full items-center justify-center text-gray-400 bg-gray-50">
                  <i class="fa fa-box text-base" />
                </div>
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex justify-between items-start gap-2">
                  <div class="min-w-0">
                    <h3 class="font-bold text-gray-900 line-clamp-2 text-sm">{{ product.name }}</h3>
                    <div class="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span v-if="product.type_product === 'insumo'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                        <i class="fa fa-boxes-stacked mr-1 text-[9px]" /> Insumo
                      </span>
                      <span v-else-if="product.type_product === 'recipe'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                        <i class="fa fa-utensils mr-1 text-[9px]" /> Receta
                      </span>
                      <span v-else-if="product.type_product === 'service'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                        Servicio
                      </span>
                    </div>
                  </div>
                  <span 
                    class="ml-2 px-2 py-0.5 rounded-full text-xs font-bold shrink-0"
                    :class="getGainClass(calculateGain(product))"
                  >
                    {{ formatCurrency(calculateGain(product)) }}
                  </span>
                </div>
                <p class="text-xs text-blue-600 font-medium mt-1">{{ product.code }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 text-sm text-gray-600 pt-1">
              <div>
                <span class="block text-xs text-gray-400">Precio USD</span>
                <span class="font-bold text-gray-900">{{ formatCurrency(product?.prices?.[0]?.price) }}</span>
              </div>
              <div>
                <span class="block text-xs text-gray-400">Precio Bs</span>
                <span class="font-bold text-gray-900">{{ formatCurrencyBs((product?.prices?.[0]?.price || 0) * Number(change)) }}</span>
              </div>
            </div>

            <div v-if="product.product_deposits && product.product_deposits.length" class="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
              <span 
                v-for="deposit in product.product_deposits"
                :key="deposit.id"
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
              >
                {{ deposit?.deposit?.name }}: <strong class="ml-1 text-gray-800">{{ deposit?.quantity }}</strong>
              </span>
            </div>
          </Link>
        </div>

        <!-- Mobile Empty State -->
        <div v-if="!products || (products.data && products.data.length === 0)" class="p-8 text-center text-gray-400">
          <i class="fa fa-box-open text-4xl mb-3 text-gray-300" />
          <h3 class="text-base font-bold text-gray-800">No hay productos disponibles</h3>
          <p class="text-xs text-gray-500 mt-1">Crea un producto nuevo o importa un listado masivo.</p>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/70 text-left border-b border-gray-100">
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Código</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider max-w-[300px]">Nombre</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Categoría</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Marca</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Cantidades</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Costo</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Precio</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Precio Bs</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-center">Ganancia</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="product in (products?.data || [])" 
              :key="product.id"
              class="group hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4">
                <Link :href="`/products/${product.id}`" class="flex items-center gap-3">
                  <img 
                    v-if="product.photo_url || product.photo"
                    :src="product.photo_url || `/img/${product.photo}`"
                    :alt="product.name"
                    class="w-10 h-10 rounded-lg object-cover bg-gray-100 border border-gray-200 shrink-0"
                  />
                  <div v-else class="w-10 h-10 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center shrink-0 border border-gray-200">
                    <i class="fa fa-box text-sm" />
                  </div>
                  <span class="font-medium text-blue-600 group-hover:text-blue-700 text-sm">{{ product.code }}</span>
                </Link>
              </td>
              
              <td class="px-6 py-4 max-w-[300px] whitespace-normal">
                <Link :href="`/products/${product.id}`" class="text-gray-900 font-medium hover:text-orange-600 line-clamp-2 text-sm">
                  {{ product.name }}
                </Link>
                <div class="mt-1 flex items-center gap-1.5">
                  <span v-if="product.type_product === 'insumo'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                    <i class="fa fa-boxes-stacked mr-1 text-[9px]" /> Insumo
                  </span>
                  <span v-else-if="product.type_product === 'recipe'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                    <i class="fa fa-utensils mr-1 text-[9px]" /> Receta
                  </span>
                  <span v-else-if="product.type_product === 'service'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                    Servicio
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ product?.product_category?.name || '-' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ product?.brand?.name || '-' }}
              </td>

              <td class="px-6 py-4">
                <div class="flex flex-col gap-1">
                  <span 
                    v-for="deposit in (product.product_deposits || [])"
                    :key="deposit.id"
                    class="text-xs text-gray-600"
                  >
                    <span class="font-bold text-gray-900">{{ deposit?.quantity }}</span> en {{ deposit?.deposit?.name }}
                  </span>
                  <span v-if="!product.product_deposits || !product.product_deposits.length" class="text-xs text-gray-400">
                    {{ product.quantity ?? 0 }} en total
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 text-right text-gray-600 text-sm">
                {{ formatCurrency(product?.cost) }}
              </td>

              <td class="px-6 py-4 text-right font-bold text-gray-900 text-sm">
                {{ formatCurrency(product?.prices?.[0]?.price) }}
              </td>

              <td class="px-6 py-4 text-right text-gray-600 text-sm">
                {{ formatCurrencyBs((product?.prices?.[0]?.price || 0) * Number(change)) }}
              </td>

              <td class="px-6 py-4 text-center">
                <span 
                  class="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold"
                  :class="getGainClass(calculateGain(product))"
                >
                  {{ formatCurrency(calculateGain(product)) }}
                </span>
              </td>
            </tr>

            <!-- Empty State Desktop -->
            <tr v-if="!products || (products.data && products.data.length === 0)">
              <td colspan="9" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-box-open text-2xl opacity-50" />
                  </div>
                  <h3 class="text-base font-bold text-gray-900 mb-1">No hay productos disponibles</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Empieza por crear un nuevo producto en el inventario o realiza una importación masiva.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="paginationLinks && paginationLinks.length" class="mt-6 flex justify-center">
      <pagination 
        :links="paginationLinks"
        :query-params="currentQueryParams"
      />
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'ProductTable',

  components: {
    Link,
    Pagination,
  },

  props: {
    products: {
      type: Object,
      required: true,
    },
    change: {
      type: [Number, String],
      default: 1,
    },
    currentQueryParams: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    paginationLinks() {
      return this.products?.meta?.links || this.products?.links || []
    },
  },

  methods: {
    calculateGain(product) {
      const price = Number(product?.prices?.[0]?.price || 0)
      const cost = Number(product?.cost || 0)
      return price - cost
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value || 0)
    },

    formatCurrencyBs(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'VES',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value || 0)
    },

    getGainClass(gain) {
      if (gain > 0) return 'bg-green-100 text-green-700'
      if (gain < 0) return 'bg-red-100 text-red-700'
      return 'bg-gray-100 text-gray-700'
    },
  },
}
</script>
